import React from 'react';
import { 
    Card, 
    CardContent, 
    Typography, 
    Box, 
    Button,
    CircularProgress,
    Alert
} from '@mui/material';
import { TrendingUp, Email, CheckCircle, Flag } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useRegressionProspectCounts } from '../hooks/useRegressionProspects';

export const RegressionProspectsWidget: React.FC = () => {
    const { counts, loading, error } = useRegressionProspectCounts();
    const navigate = useNavigate();

    if (loading) {
        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="center" py={2}>
                        <CircularProgress size={24} />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Alert severity="error" sx={{ py: 1 }}>
                        Error loading prospects
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    const handleStatusClick = (status: string) => {
        // Navigate to the dedicated regression prospects page
        navigate('/regression-prospects');
    };

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent sx={{ pb: 2 }}>
                <Box display="flex" alignItems="center" mb={2}>
                    <TrendingUp color="warning" sx={{ mr: 1, fontSize: 20 }} />
                    <Typography variant="h6" fontWeight="bold" fontSize="0.9rem">
                        REGRESSION PROSPECTS
                    </Typography>
                </Box>

                <Box display="flex" flexDirection="column" gap={1}>
                    {/* Pending */}
                    <Button
                        variant="text"
                        startIcon={<Email />}
                        onClick={() => handleStatusClick('pending')}
                        sx={{
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                            py: 0.5,
                            px: 1,
                            fontSize: '0.85rem',
                            color: 'text.primary',
                            '&:hover': {
                                backgroundColor: 'action.hover'
                            }
                        }}
                    >
                        PENDING ({counts.pending})
                    </Button>

                    {/* Contacted */}
                    <Button
                        variant="text"
                        startIcon={<CheckCircle />}
                        onClick={() => handleStatusClick('contacted')}
                        sx={{
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                            py: 0.5,
                            px: 1,
                            fontSize: '0.85rem',
                            color: 'text.primary',
                            '&:hover': {
                                backgroundColor: 'action.hover'
                            }
                        }}
                    >
                        CONTACTED ({counts.contacted})
                    </Button>

                    {/* Completed */}
                    <Button
                        variant="text"
                        startIcon={<Flag />}
                        onClick={() => handleStatusClick('completed')}
                        sx={{
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                            py: 0.5,
                            px: 1,
                            fontSize: '0.85rem',
                            color: 'text.primary',
                            '&:hover': {
                                backgroundColor: 'action.hover'
                            }
                        }}
                    >
                        COMPLETED ({counts.completed})
                    </Button>
                </Box>

                {/* Quick Stats */}
                {counts.total > 0 && (
                    <Box mt={2} pt={1} borderTop={1} borderColor="grey.200">
                        <Typography variant="caption" color="textSecondary">
                            Total prospects: {counts.total}
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};